import React from "react";

import { Box, BoxProps, SxProps } from "@mui/material";
import styled from "@mui/material/styles/styled";

import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import { AnnotationDirection, ReactionHighlight } from "@app/common/types";

import AnnotatedTextTooltip from "./AnnotatedTextTooltip";

type AnnotationType = AnnotationDirection | "none";

const STYLES = {
  innerLeft: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "none",
    paddingLeft: 0,
  },
  innerRight: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRight: "none",
    paddingRight: 0,
  },
  byAnnotationType: {
    positive: {
      bgcolor: "shades.meadow.100",
      color: "shades.meadow.600",
      "&:hover": { bgcolor: "shades.meadow.200" },
    },
    negative: {
      bgcolor: "shades.negative.100",
      color: "shades.negative.600",
      "&:hover": { bgcolor: "shades.negative.200" },
    },
  },
};

interface AnnotationOptions {
  /** True if this annotated block has another annotated block preceding it, and the preceding block is a
   * parent annotation of this block. E.g., this is a highlighted portion inside a metric annotation */
  innerLeft?: boolean;
  /** True if this annotated block has another annotated block following it, and the following block is a
   * parent annotation of this block. E.g., this is a highlighted portion inside a metric annotation */
  innerRight?: boolean;
}

export interface AnnotatedTextProps {
  /** Whether this element is the active highlighted element */
  active?: boolean;
  annotationOptions?: AnnotationOptions;
  children: React.ReactNode;
  /** the index in the search results for this highlight if applicable */
  searchIndex?: number;
  /** If this text block is highlighted */
  highlighted?: boolean;
  sx?: SxProps;
  tooltipContent?: ReactionHighlight["score"];
  type?: AnnotationType;
}

const BaseTextWrapper = styled(
  ({ highlighted: _, ...props }: BoxProps & { highlighted?: boolean }) => (
    <Box component="span" {...props} />
  )
)(({ theme, highlighted }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  display: "inline",
  ...(highlighted && {
    backgroundColor: theme.palette.chart.yellow.light,
    border: `1px solid ${theme.palette.chart.yellow.light}`,
    borderRadius: "2px",
    color: theme.palette.shades.neutral[800],
    padding: 0,
  }),
  "&.active": {
    backgroundColor: theme.palette.chart.yellow.main,
    border: `1px solid ${theme.palette.chart.yellow.dark}`,
    borderRadius: "2px",
    color: theme.palette.shades.neutral[800],
    padding: 0,
  },
}));

const BaseAnnotatedTextWrapper = styled(BaseTextWrapper)(() => ({
  borderRadius: "4px",
  padding: "0 2px",
  "&:hover": { cursor: "pointer" },
}));

const combineRawStyleWithOptions = (
  style: object,
  options: AnnotationOptions
) => {
  let combinedStyle = style;

  if (options.innerLeft) {
    combinedStyle = { ...combinedStyle, ...STYLES.innerLeft };
  }

  if (options.innerRight) {
    combinedStyle = { ...combinedStyle, ...STYLES.innerRight };
  }

  return combinedStyle;
};

const getStyle = (
  highlighted: boolean,
  type: AnnotationType,
  options: AnnotationOptions
) => {
  // highlight takes precedence over other annotation styles
  if (highlighted) {
    return combineRawStyleWithOptions({}, options);
  }

  if (type === "none") return {};

  const annotationTypeStyle = STYLES.byAnnotationType[type];
  return combineRawStyleWithOptions(annotationTypeStyle, options);
};

const AnnotatedText = ({
  children,
  active = false,
  annotationOptions = {},
  highlighted = false,
  sx,
  tooltipContent,
  type = "none",
  searchIndex,
}: AnnotatedTextProps) => {
  const style = getStyle(highlighted, type, annotationOptions);

  const BaseWrapper =
    type === "none" ? BaseTextWrapper : BaseAnnotatedTextWrapper;

  const textContent = (
    <BaseWrapper
      highlighted={highlighted}
      sx={{ ...style, ...sx }}
      data-search-index={searchIndex}
      className={active ? "active" : undefined}
    >
      {children}
    </BaseWrapper>
  );

  if (tooltipContent) {
    return (
      <AnnotatedTextTooltip content={tooltipContent}>
        {textContent}
      </AnnotatedTextTooltip>
    );
  }

  return textContent;
};

const SearchCompatibleAnnotatedText = (props: AnnotatedTextProps) => {
  // TODO - figure out a better way to detect whether the current text is "active" that does
  //  not break the reusable-ness of this component
  const isActive = useSearchStore(
    (state) =>
      props.searchIndex != null &&
      state.activeSearchResultIndex === props.searchIndex
  );

  return <AnnotatedText {...props} active={isActive} />;
};

export { SearchCompatibleAnnotatedText };

export default AnnotatedText;
